import { useTranslation } from 'react-i18next';

/**
 * Lo que se espera ganar, antes de haber vendido nada.
 *
 * Es lo único de Métricas que dice algo desde el primer día, porque no sale de
 * los pedidos —que pueden ser cero— sino de los precios y costes que ya están
 * puestos en los productos.
 *
 * ## Lo que se corrigió de la primera versión
 *
 * La persona que usa esto lo dijo sin rodeos: *«las informaciones están
 * demasiado dispersas, casi no se entiende qué pertenece a quién, y los
 * conceptos de los porcentajes son muy técnicos»*. Tenía razón en las dos.
 *
 * **La dispersión** venía de poner la etiqueta a la izquierda y el número al
 * borde derecho de una columna ancha: entre «Envío» y «$9.97» había media
 * pantalla de nada, y el ojo tiene que saltar. Ahora las filas viven en una
 * columna estrecha, con el número pegado a su nombre.
 *
 * **Lo técnico** era llamar a las cosas por su fórmula. «Margen medio esperado:
 * 23,1 %» obliga a saber qué es un margen; «de cada $23.15 que cobras, te
 * quedas $5.34» no obliga a saber nada. El porcentaje sigue estando, debajo y
 * en pequeño, para quien lo quiera.
 *
 * Y los tramos ya no se llaman por su rango: «Poco margen», «Margen normal» y
 * «Buen margen», con el rango como aclaración al lado.
 *
 * ## Por qué una sola barra y no tres segmentos
 *
 * La primera idea fue partir el precio en coste + envío + ganancia dentro de
 * una barra. El validador de paletas lo tumbó, y con motivo: en modo oscuro la
 * banda de luminosidad admitida es estrecha, y **dos tonos del mismo color no
 * llegan a separarse lo suficiente** ni para quien ve todos los colores. Usar
 * dos tonos distintos habría roto la idea de «esto son dos costes».
 *
 * Así que una sola barra con un solo color, que es la forma correcta para
 * enseñar qué parte del total te quedas, y las tres cifras en una lista donde
 * no compiten con nada.
 *
 * ## El detalle que la hace honesta
 *
 * El cálculo usa **solo los productos con envío conocido**, y los demás se
 * cuentan aparte y se dicen. Meterlos tratando el envío como cero da un número
 * más bonito y falso: sobre los datos reales de esta cuenta, 25,0 % contando
 * los que no sabemos frente a 23,1 % contando solo los que sí.
 */

const dinero = (n) => `$${Number(n || 0).toFixed(2)}`;
const pct = (n) => `${Number(n || 0).toFixed(1)}%`;

/** Los tres tramos. El nombre dice qué significa; el rango, dónde empieza. */
const TRAMOS = [
    { clave: 'bajo', barra: 'bg-danger-500', numero: 'text-danger-500' },
    { clave: 'medio', barra: 'bg-warning-500', numero: 'text-warning-600 dark:text-warning-500' },
    { clave: 'alto', barra: 'bg-success-500', numero: 'text-success-500' }
];

export default function RentabilidadEsperada({ datos }) {
    const { t } = useTranslation();

    if (!datos) return null;

    // `sinComision` no lo manda un servidor anterior a este cambio: cero.
    const { evaluables, sinPrecio, sinFlete, sinComision = 0, margenMedio, medias, tramos, enPerdida } = datos;

    if (!evaluables) {
        return (
            <div className="card">
                <h3 className="section-title mb-2">{t('analytics.expected_title')}</h3>
                <p className="text-sm text-slate-500">{t('analytics.expected_empty')}</p>
            </div>
        );
    }

    const total = tramos.bajo + tramos.medio + tramos.alto;

    return (
        <div className="card">
            <h3 className="section-title mb-1">{t('analytics.expected_title')}</h3>
            <p className="text-sm text-slate-500 mb-6">
                {t('analytics.expected_subtitle', { count: evaluables })}
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Qué pasa en una venta media. */}
                <div>
                    <p className="text-lg text-slate-900 dark:text-white leading-snug">
                        {t('analytics.expected_headline_1')}{' '}
                        <span className="font-bold">{dinero(medias.precio)}</span>{' '}
                        {t('analytics.expected_headline_2')}{' '}
                        <span className="font-bold text-success-500">{dinero(medias.ganancia)}</span>.
                    </p>

                    {/* Una barra, un color: qué parte del precio acaba siendo
                        tuya. Lleva el porcentaje escrito al lado para quien
                        prefiera la cifra. */}
                    <div className="flex items-center gap-3 mt-4">
                        <span className="flex-1 h-3 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                            <span
                                className="block h-full bg-success-500 rounded-full"
                                style={{ width: `${Math.max(0, Math.min(100, margenMedio))}%` }}
                            />
                        </span>
                        <span className="text-sm font-semibold text-success-500 w-14 text-right">{pct(margenMedio)}</span>
                    </div>

                    {/* Columna estrecha a propósito: el número va pegado a su
                        nombre, no al borde de la tarjeta. */}
                    <dl className="mt-6 max-w-xs space-y-2 text-sm">
                        {[
                            ['expected_row_price', medias.precio, 'text-slate-900 dark:text-white'],
                            ['expected_row_cost', -medias.coste, 'text-slate-600 dark:text-slate-300'],
                            ['expected_row_shipping', -medias.flete, 'text-slate-600 dark:text-slate-300'],
                            // Lo que se queda el destino. Sin esta fila, la
                            // resta de abajo no cuadraba con las de arriba. Un
                            // servidor anterior a este cambio no la manda, y
                            // entonces la fila no se pinta en vez de decir $0.
                            ...(medias.comision !== null && medias.comision !== undefined
                                ? [['expected_row_fee', -medias.comision, 'text-slate-600 dark:text-slate-300']]
                                : [])
                        ].map(([clave, valor, clase]) => (
                            <div key={clave} className="flex justify-between gap-4">
                                <dt className="text-slate-500">{t(`analytics.${clave}`)}</dt>
                                <dd className={clase}>
                                    {valor < 0 ? `− ${dinero(-valor)}` : dinero(valor)}
                                </dd>
                            </div>
                        ))}

                        <div className="flex justify-between gap-4 pt-2 border-t border-slate-200 dark:border-slate-700">
                            <dt className="text-slate-600 dark:text-slate-300 font-medium">
                                {t('analytics.expected_row_profit')}
                            </dt>
                            <dd className="text-success-500 font-semibold">{dinero(medias.ganancia)}</dd>
                        </div>
                    </dl>
                </div>

                {/* Cómo está repartido el catálogo. */}
                <div>
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                        {t('analytics.expected_spread')}
                    </p>
                    <p className="text-xs text-slate-500 mb-4">{t('analytics.expected_spread_why')}</p>

                    <ul className="space-y-3">
                        {TRAMOS.map(({ clave, barra, numero }) => {
                            const n = tramos[clave];
                            return (
                                <li key={clave}>
                                    <div className="flex items-baseline justify-between gap-3 mb-1">
                                        <span className="text-sm text-slate-700 dark:text-slate-200">
                                            {t(`analytics.tramo_${clave}`)}
                                            <span className="text-xs text-slate-400 ml-2">
                                                {t(`analytics.tramo_${clave}_rango`)}
                                            </span>
                                        </span>
                                        <span className={`text-sm font-semibold shrink-0 ${n ? numero : 'text-slate-400'}`}>
                                            {n}
                                        </span>
                                    </div>
                                    <span className="block h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                                        <span
                                            className={`block h-full ${barra} rounded-full`}
                                            style={{ width: total ? `${(n / total) * 100}%` : 0 }}
                                        />
                                    </span>
                                </li>
                            );
                        })}
                    </ul>

                    {/* Lo que falta por saber, dicho y no escondido. */}
                    {(enPerdida > 0 || sinFlete > 0 || sinPrecio > 0 || sinComision > 0 || tramos.bajo > 0) && (
                        <ul className="mt-5 space-y-1.5 text-xs">
                            {enPerdida > 0 && (
                                <li className="text-danger-500 font-medium">
                                    {t('analytics.expected_losing', { count: enPerdida })}
                                </li>
                            )}
                            {tramos.bajo > 0 && (
                                <li className="text-slate-500">{t('analytics.tramo_bajo_aviso')}</li>
                            )}
                            {sinFlete > 0 && (
                                <li className="text-warning-600 dark:text-warning-500">
                                    {t('analytics.expected_no_shipping', { count: sinFlete })}
                                </li>
                            )}
                            {sinPrecio > 0 && (
                                <li className="text-slate-500">
                                    {t('analytics.expected_no_price', { count: sinPrecio })}
                                </li>
                            )}
                            {sinComision > 0 && (
                                <li className="text-warning-600 dark:text-warning-500">
                                    {t('analytics.expected_no_fee', { count: sinComision })}
                                </li>
                            )}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}
